import { useLayoutEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTimelineContext } from 'context/TimelineContext'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import {
  getDistinctZIndicesDescending,
  zIndexToVisualRowRank
} from 'utils/timelineZIndex'
import * as Styled from './styled'
import { DATA_DROP_LINE_INDEX, TIMELINE_TRACK_HEIGHT } from 'consts'

const defaultState = {
  top: 0,
  height: TIMELINE_TRACK_HEIGHT
}

const DropIndicator: React.FC = () => {
  const { dropState } = useTimelineContext()
  const {
    isDragOver,
    positionPx,
    widthPx,
    rowZIndex,
    targetGapAfterLineIndex
  } = dropState
  const mediaItems = useSelector(projectDataSelectors.mediaItems)
  const ref = useRef<HTMLDivElement>(null)
  const [{ top, height }, setPosition] = useState(defaultState)

  useLayoutEffect(() => {
    if (!isDragOver || !ref.current) return
    const orderedZs = getDistinctZIndicesDescending(mediaItems)
    if (rowZIndex === null || !orderedZs.includes(rowZIndex)) {
      setPosition(defaultState)
      return
    }
    const lineIndex = zIndexToVisualRowRank(rowZIndex, orderedZs)
    const rowEl = document.querySelector<HTMLElement>(
      `[${DATA_DROP_LINE_INDEX}="${lineIndex}"]`
    )
    const containingBlock = ref.current.offsetParent as HTMLElement | null
    if (!rowEl || !containingBlock) {
      setPosition(defaultState)
      return
    }
    const rowRect = rowEl.getBoundingClientRect()
    const parentRect = containingBlock.getBoundingClientRect()
    setPosition({
      top: rowRect.top - parentRect.top,
      height: rowRect.height
    })
  }, [isDragOver, rowZIndex, mediaItems, positionPx])

  // During a gap drag the precise X position is meaningless (a new row will
  // be created), so the indicator would only mislead. The `BetweenLinesGap`
  // highlight is the visual feedback for gap drops.
  if (
    !isDragOver ||
    positionPx === null ||
    widthPx === null ||
    targetGapAfterLineIndex !== null
  ) {
    return null
  }

  return (
    <Styled.Line
      ref={ref}
      $left={positionPx}
      $width={widthPx}
      $top={top}
      $height={height}
    />
  )
}

export default DropIndicator
